import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import RoleGuard from './RoleGuard';
import UserMenu from './UserMenu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminUsersPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [deleteConfirmUser, setDeleteConfirmUser] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users:', error);
      if (error.response?.status === 403) {
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, role) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { role });
      setEditingUser(null);
      setNewRole('');
      loadUsers(); // Reload users
    } catch (error) {
      console.error('Failed to update role:', error);
      alert('Ошибка при изменении роли: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      setDeleteConfirmUser(null);
      loadUsers(); // Reload users
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert('Ошибка при удалении пользователя: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleResetPassword = async (userId, userEmail) => {
    const newPassword = prompt('Введите новый пароль для пользователя:');
    if (!newPassword || newPassword.length < 6) {
      alert('Пароль должен содержать минимум 6 символов');
      return;
    }

    try {
      await axios.post(`${API}/admin/reset-user-password`, {
        user_id: userId,
        new_password: newPassword
      });
      alert(`Пароль для ${userEmail} успешно сброшен`);
    } catch (error) {
      console.error('Failed to reset password:', error);
      alert('Ошибка при сбросе пароля: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const roleLabels = {
    'admin': 'Администратор',
    'lead': 'Руководитель', 
    'manager': 'Менеджер',
    'viewer': 'Наблюдатель'
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'lead': return 'bg-blue-100 text-blue-800';
      case 'manager': return 'bg-green-100 text-green-800';
      case 'viewer': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <RoleGuard allowedRoles={['admin']}>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        {/* Navigation */}
        <nav className="bg-white/80 backdrop-blur-lg shadow-sm border-b border-white/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-20">
              <div className="flex items-center">
                <div className="flex items-center min-w-0">
                  <img 
                    src="/profland-logo-new.jpg" 
                    alt="Profland CRM" 
                    className="h-10 w-auto mr-4 flex-shrink-0"
                  />
                  <h1 className="text-xl font-semibold text-gray-900 whitespace-nowrap">Profland CRM</h1>
                </div>
                <div className="hidden md:ml-10 md:flex md:space-x-8">
                  <Link to="/dashboard" className="nav-link">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                    </svg>
                    Дашборд
                  </Link>
                  <Link to="/admin/users" className="nav-link active">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                    </svg>
                    Пользователи
                  </Link>
                  <Link to="/admin/settings" className="nav-link">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Настройки
                  </Link>
                </div>
              </div>
              <div className="flex items-center">
                <UserMenu />
              </div>
            </div>
          </div>
        </nav>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Управление пользователями
              </h1>
              <p className="text-gray-600">
                Управление ролями и правами доступа пользователей
              </p>
            </div>

            {/* Users Table */}
            <div className="bg-white/80 backdrop-blur-lg rounded-xl shadow-sm border border-white/20 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  Пользователи системы ({users.length})
                </h3>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Пользователь
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Роль
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Проекты
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Действия
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((userItem) => (
                      <tr key={userItem.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
                                <span className="text-white font-medium text-sm">
                                  {userItem.name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {userItem.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {userItem.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingUser === userItem.id ? (
                            <div className="flex items-center space-x-2">
                              <select
                                value={newRole}
                                onChange={(e) => setNewRole(e.target.value)}
                                className="text-sm border border-gray-300 rounded-md px-2 py-1"
                              >
                                <option value="">Выберите роль</option>
                                <option value="admin">Администратор</option>
                                <option value="lead">Руководитель</option>
                                <option value="manager">Менеджер</option>
                                <option value="viewer">Наблюдатель</option>
                              </select>
                              <button
                                onClick={() => handleRoleChange(userItem.id, newRole)}
                                className="text-green-600 hover:text-green-900 text-sm"
                                disabled={!newRole}
                              >
                                ✓
                              </button>
                              <button
                                onClick={() => {
                                  setEditingUser(null);
                                  setNewRole('');
                                }}
                                className="text-gray-600 hover:text-gray-900 text-sm"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(userItem.role)}`}>
                              {roleLabels[userItem.role] || userItem.role}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div>
                            Владелец: {userItem.owned_projects}
                          </div>
                          <div>
                            Назначен: {userItem.assigned_projects}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          {userItem.id !== user.id && (
                            <>
                              <button
                                onClick={() => {
                                  setEditingUser(userItem.id);
                                  setNewRole(userItem.role);
                                }}
                                className="text-indigo-600 hover:text-indigo-900"
                                disabled={editingUser === userItem.id}
                              >
                                Изменить роль
                              </button>
                              <button
                                onClick={() => handleResetPassword(userItem.id, userItem.email)}
                                className="text-blue-600 hover:text-blue-900 mr-3"
                              >
                                Сбросить пароль
                              </button>
                              <button
                                onClick={() => setDeleteConfirmUser(userItem)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Удалить
                              </button>
                            </>
                          )}
                          {userItem.id === user.id && (
                            <span className="text-gray-400">Текущий пользователь</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </main>

        {/* Delete Confirmation Modal */}
        {deleteConfirmUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md mx-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Подтвердите удаление
              </h3>
              <p className="text-gray-600 mb-6">
                Вы уверены, что хотите удалить пользователя "{deleteConfirmUser.name}"? 
                Это действие нельзя отменить.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setDeleteConfirmUser(null)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Отмена
                </button>
                <button
                  onClick={() => handleDeleteUser(deleteConfirmUser.id)}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Удалить
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </RoleGuard>
  );
};

export default AdminUsersPage;